const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';
const SCROLL_ESCAPE_HATCH_ATTR = 'data-scroll-escape-hatch-applied';
const SCROLL_ESCAPE_HATCH_DEFAULT_DELAYS_MS = [2000, 6000];
const SCROLL_ESCAPE_HATCH_MIN_SIZE_PX = 50;
const SCROLL_ESCAPE_HATCH_MAX_OPACITY = 0.02;

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

function _isStuckInvisibleScrollTrigger(el) {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (!el.classList.contains(SCROLL_ANIMATION_TRIGGER_CLASSNAME)) return false;
  if (el.hasAttribute(SCROLL_ESCAPE_HATCH_ATTR)) return false;

  const rect = el.getBoundingClientRect();
  if (rect.width < SCROLL_ESCAPE_HATCH_MIN_SIZE_PX || rect.height < SCROLL_ESCAPE_HATCH_MIN_SIZE_PX) return false;

  const cs = window.getComputedStyle(el);
  if (cs.display === 'none' || cs.visibility === 'hidden') return false;
  const opacity = parseFloat(cs.opacity || '1');
  if (!(opacity <= SCROLL_ESCAPE_HATCH_MAX_OPACITY)) return false;

  // Only unstick elements that are actually within the current viewport,
  // so we don't force-reveal content that should remain offscreen.
  const inViewport = rect.bottom > 0 && rect.top < window.innerHeight;
  if (!inViewport) return false;

  return true;
}

function runScrollAnimationEscapeHatch(rootEl = document) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const candidates = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (candidates.length === 0) return;

  candidates.forEach((el) => {
    if (!_isStuckInvisibleScrollTrigger(el)) return;
    el.setAttribute(SCROLL_ESCAPE_HATCH_ATTR, 'true');
    // Minimal unstick: cancel offscreen state; CSS already defines cancel to force visibility.
    el.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    el.classList.add(SCROLL_ANIMATION_CANCEL_CLASSNAME);
  });
}

function _scheduleEscapeHatchRuns(delaysMs = SCROLL_ESCAPE_HATCH_DEFAULT_DELAYS_MS) {
  delaysMs.forEach((delay) => {
    window.setTimeout(() => runScrollAnimationEscapeHatch(document), delay);
  });
}

function _installScrollTriggerMutationObserver() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (window.__scrollTriggerMutationObserverInstalled) return;
  window.__scrollTriggerMutationObserverInstalled = true;

  let debounceTimer = null;
  const observer = new MutationObserver((mutations) => {
    let sawScrollTrigger = false;
    for (const m of mutations) {
      for (const n of m.addedNodes) {
        if (!(n instanceof HTMLElement)) continue;
        if (n.classList?.contains?.(SCROLL_ANIMATION_TRIGGER_CLASSNAME) || n.querySelector?.(`.${SCROLL_ANIMATION_TRIGGER_CLASSNAME}`)) {
          sawScrollTrigger = true;
          break;
        }
      }
      if (sawScrollTrigger) break;
    }
    if (!sawScrollTrigger) return;

    if (debounceTimer) window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      try {
        initializeScrollAnimationTrigger(document, false);
      } catch (e) {
        // Never let animation init failures block the page.
        // eslint-disable-next-line no-console
        console.warn('[scroll-trigger] initializeScrollAnimationTrigger failed', e);
      }
      runScrollAnimationEscapeHatch(document);
    }, 100);
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  const percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
  _installScrollTriggerMutationObserver();
});

// After full load (and after a short delay), apply a minimal escape hatch for any stuck invisible elements.
window.addEventListener('load', () => {
  _scheduleEscapeHatchRuns();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
